import { useCallback, useEffect, useState } from "react";
import {
  JOURNEY_STATE_ORDINAL,
  type AdminOverviewMetrics,
  type JourneyState,
  type TreatmentState,
} from "@care-by-ella/contracts";
import { fetchOverviewMetrics } from "@/lib/metrics-api";
import { journeyStateLabel, treatmentStateLabel } from "@/lib/state-labels";

// TES-580 — "how are we doing", as numbers. The admin's landing page.
//
// Everything on this page is a count. There is no way from here to a
// patient: the admin role has no clinical relationship, and a roster
// "for reference" is a PDL problem (see ADMIN_SECTIONS in AppShell). The
// numbers are computed live on each load — no snapshot, no period picker
// yet; the 7/30-day rows are the only time dimension in v1.

const fmt = new Intl.NumberFormat("sv-SE");

function Stat({
  label,
  value,
  hint,
}: {
  label: string;
  value: number;
  hint?: string;
}) {
  return (
    <div className="border border-surfaceMuted rounded-md p-md bg-surface flex flex-col gap-xs min-w-0">
      <p className="text-xs uppercase tracking-wide text-textSecondary">
        {label}
      </p>
      <p className="font-display text-3xl tabular-nums">{fmt.format(value)}</p>
      {hint ? <p className="text-xs text-textSecondary">{hint}</p> : null}
    </div>
  );
}

function Section({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section className="flex flex-col gap-sm">
      <h2 className="text-sm font-medium">{title}</h2>
      {children}
    </section>
  );
}

function StatGrid({ children }: { children: React.ReactNode }) {
  return (
    <div className="grid gap-sm grid-cols-2 md:grid-cols-3 lg:grid-cols-5">
      {children}
    </div>
  );
}

// One row per step of the ladder: how many stand exactly here, and how many
// have come at least this far. The ladder can be skipped, so "at least" is
// the number that answers "how many booked a meeting" — the histogram bucket
// alone would miss everyone who has moved on.
function Funnel({ journey }: { journey: AdminOverviewMetrics["journey"] }) {
  const top = journey.reachedAtLeast[0]?.patients ?? 0;
  return (
    <div className="border border-surfaceMuted rounded-md bg-surface overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-xs uppercase tracking-wide text-textSecondary text-left">
            <th className="px-md py-sm font-medium">Steg</th>
            <th className="px-md py-sm font-medium text-right">Står här nu</th>
            <th className="px-md py-sm font-medium text-right">
              Har nått minst hit
            </th>
            <th className="px-md py-sm font-medium text-right">Andel</th>
          </tr>
        </thead>
        <tbody>
          {journey.reachedAtLeast.map(({ state, patients }) => (
            <tr key={state} className="border-t border-surfaceMuted">
              <td className="px-md py-sm">
                {journeyStateLabel(state, { acrossPatients: true })}
              </td>
              <td className="px-md py-sm text-right tabular-nums">
                {fmt.format(journey.byState[state] ?? 0)}
              </td>
              <td className="px-md py-sm text-right tabular-nums">
                {fmt.format(patients)}
              </td>
              <td className="px-md py-sm text-right tabular-nums text-textSecondary">
                {top > 0 ? `${Math.round((patients / top) * 100)} %` : "–"}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// Paused/ended sit outside the ladder (no ordinal) — shown as their own row
// so the histogram still adds up to the patient total.
function overlayStates(byState: Record<string, number>): JourneyState[] {
  return (Object.keys(byState) as JourneyState[]).filter(
    (s) => JOURNEY_STATE_ORDINAL[s] < 0,
  );
}

export function OverviewRoute() {
  const [data, setData] = useState<AdminOverviewMetrics | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      setData(await fetchOverviewMetrics());
    } catch (err) {
      setData(null);
      setError(err instanceof Error ? err.message : "okänt fel");
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const header = (
    <div className="flex flex-col gap-xs">
      <h1 className="font-display text-3xl tracking-tight">Översikt</h1>
      <p className="text-textSecondary text-sm max-w-xl">
        Hur det går, i siffror. Testpatienter räknas inte; raderade patienter
        redovisas separat. Inga patientuppgifter visas här.
      </p>
    </div>
  );

  if (error) {
    return (
      <div className="flex flex-col gap-lg">
        {header}
        <div className="border border-danger rounded-md p-md text-sm bg-surface flex flex-col gap-sm items-start">
          <p className="text-danger">Siffrorna kunde inte hämtas ({error}).</p>
          <button
            type="button"
            onClick={() => void load()}
            className="px-md py-sm rounded-md border border-surfaceMuted"
          >
            Försök igen
          </button>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="flex flex-col gap-lg">
        {header}
        <div
          className="grid gap-sm grid-cols-2 md:grid-cols-3 lg:grid-cols-5"
          aria-busy="true"
        >
          {Array.from({ length: 5 }, (_, i) => (
            <div
              key={i}
              className="h-24 rounded-md border border-surfaceMuted bg-surfaceMuted/30 animate-pulse"
            />
          ))}
        </div>
      </div>
    );
  }

  const { patients, journey, meetings, subscription, treatment, leads } = data;
  const overlays = overlayStates(journey.byState);
  const computedAt = new Date(data.computedAt).toLocaleString("sv-SE", {
    dateStyle: "short",
    timeStyle: "short",
  });

  return (
    <div className="flex flex-col gap-lg">
      <div className="flex items-start justify-between gap-md flex-wrap">
        {header}
        <button
          type="button"
          onClick={() => void load()}
          className="px-md py-sm rounded-md border border-surfaceMuted text-sm shrink-0"
        >
          Uppdatera
        </button>
      </div>

      {patients.total === 0 ? (
        <div className="border border-surfaceMuted rounded-md p-lg bg-surface flex flex-col gap-sm items-start">
          <p className="text-sm font-medium">Inga patienter ännu</p>
          <p className="text-sm text-textSecondary max-w-lg">
            Siffrorna fylls på när den första patienten registrerar sig i appen.
          </p>
        </div>
      ) : null}

      <Section title="Patienter">
        <StatGrid>
          <Stat label="Patienter" value={patients.total} />
          <Stat label="Nya senaste 7 dagarna" value={patients.newLast7Days} />
          <Stat label="Nya senaste 30 dagarna" value={patients.newLast30Days} />
          <Stat
            label="Raderade"
            value={patients.anonymised}
            hint="Anonymiserade på begäran, räknas inte ovan"
          />
          <Stat
            label="Webbleads"
            value={leads.total}
            hint={`${fmt.format(leads.converted)} blev patienter`}
          />
        </StatGrid>
      </Section>

      <Section title="Var i resan patienterna står">
        <Funnel journey={journey} />
        {overlays.some((s) => (journey.byState[s] ?? 0) > 0) ? (
          <p className="text-xs text-textSecondary">
            Utanför stegen:{" "}
            {overlays
              .map(
                (s) =>
                  `${journeyStateLabel(s, { acrossPatients: true })} ${fmt.format(journey.byState[s] ?? 0)}`,
              )
              .join(" · ")}
          </p>
        ) : null}
      </Section>

      <Section title="Första mötet">
        <StatGrid>
          <Stat
            label="Har bokat"
            value={meetings.patientsBookedFirstConsult}
            hint="Patienter med ett bokat första möte"
          />
          <Stat
            label="Har betalat mötesavgiften"
            value={meetings.patientsPaidConsultFee}
          />
          <Stat label="Genomförda möten" value={meetings.completed} />
          <Stat label="Uteblivna" value={meetings.noShow} />
          <Stat label="Avbokade" value={meetings.cancelled} />
        </StatGrid>
      </Section>

      <Section title="Abonnemang">
        <StatGrid>
          <Stat
            label="Har fått länk"
            value={subscription.patientsLinkIssued}
            hint="Abonnemangslänk utfärdad av läkare"
          />
          <Stat
            label="Har löst in länken"
            value={subscription.patientsLinkRedeemed}
          />
          <Stat
            label="Betalar"
            value={subscription.paying}
            hint="Aktivt eller i provperiod hos Stripe"
          />
          <Stat label="Förfallen betalning" value={subscription.pastDue} />
          <Stat label="Avslutade" value={subscription.canceled} />
        </StatGrid>
      </Section>

      <Section title="Behandling">
        <StatGrid>
          {(Object.keys(treatment.byState) as TreatmentState[]).map((s) => (
            <Stat
              key={s}
              label={treatmentStateLabel(s)}
              value={treatment.byState[s] ?? 0}
            />
          ))}
        </StatGrid>
      </Section>

      <p className="text-xs text-textSecondary">Beräknat {computedAt}.</p>
    </div>
  );
}
